import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faSpinner,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import FormInput from "@/components/ui/form-input";
import { Switch } from "@/components/ui/switch/Switch";
import { Button } from "@/components/ui/button/Button";
import { Checkbox } from "@/components/ui/checkbox/Checkbox";
import {
  getAvailableDocuments,
  type AvailableDocument,
  type AvailableDocumentsResponse,
} from "@/services/api/case-checking";
import {
  availableDocumentQueueLabel,
  formatAvailableDocumentMeta,
} from "@/components/case-checking/availableDocumentMeta";

function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function getApiErrorMessage(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: unknown } } };
  const detail = ax.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: { msg?: string } | string) =>
        typeof d === "string" ? d : d.msg ?? JSON.stringify(d),
      )
      .join(", ");
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export interface AvailableDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  /**
   * Parent adds Progeny document ids to the addition queue (no immediate ingest).
   */
  onDocumentsAdded: (progenyDocumentIds: string[], labels?: Record<string, string>) => void;
  /** CRM catalogue scope; use `all` when browsing the full account catalogue. */
  scope?: "suggested" | "all";
  /** Disable add actions while a background ingest task is already running. */
  ingestBlocked?: boolean;
  ingestBlockedMessage?: string | null;
}

/**
 * Modal to browse CRM documents for the case account, search, refresh cache, and add files to the case.
 */
export function AvailableDocumentsModal({
  open,
  onClose,
  caseId,
  scope = "suggested",
  onDocumentsAdded,
  ingestBlocked = false,
  ingestBlockedMessage = null,
}: AvailableDocumentsModalProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState<AvailableDocumentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hideAdded, setHideAdded] = useState(true);
  const [addingId, _setAddingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!open) return;
    setSearchInput("");
    setDebouncedSearch("");
    setHideAdded(true);
    setError(null);
    setData(null);
    setSelectedIds(new Set());
  }, [open, caseId]);

  const fetchList = useCallback(
    async (refresh: boolean) => {
      if (!caseId) return;
      setError(null);
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const res = await getAvailableDocuments(caseId, {
          search: debouncedSearch.trim() || undefined,
          refresh,
          scope,
        });
        setData(res);
      } catch (e) {
        setData(null);
        setError(getApiErrorMessage(e));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [caseId, debouncedSearch, scope],
  );

  useEffect(() => {
    if (!open || !caseId) return;
    void fetchList(false);
  }, [open, caseId, debouncedSearch, fetchList]);

  const handleRefreshFromCrm = async () => {
    await fetchList(true);
  };

  const rows = data?.documents ?? [];
  const visibleRows = hideAdded
    ? rows.filter((d) => !d.selected_for_case)
    : rows;

  const toggleSelected = useCallback((documentId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  }, []);

  const addDocuments = useCallback(
    (documentIds: string[], labels?: Record<string, string>) => {
      if (!caseId || documentIds.length === 0 || ingestBlocked) return;
      const resolvedLabels = labels ?? {};
      const merged: Record<string, string> = {};
      for (const id of documentIds) {
        const fromCatalogue = data?.documents.find((d) => d.document_id === id);
        merged[id] =
          resolvedLabels[id] ??
          (fromCatalogue ? availableDocumentQueueLabel(fromCatalogue) : id);
      }
      onDocumentsAdded(documentIds, merged);
      onClose();
    },
    [caseId, data?.documents, ingestBlocked, onClose, onDocumentsAdded],
  );

  const handleAddOne = (doc: AvailableDocument) => {
    void addDocuments([doc.document_id], { [doc.document_id]: availableDocumentQueueLabel(doc) });
  };

  const handleAddSelected = () => {
    const ids = [...selectedIds];
    const labels: Record<string, string> = {};
    for (const id of ids) {
      const doc = data?.documents.find((d) => d.document_id === id);
      labels[id] = doc ? availableDocumentQueueLabel(doc) : id;
    }
    void addDocuments(ids, labels);
  };

  const selectedCount = selectedIds.size;
  const displayError = ingestBlockedMessage ?? error;

  return (
    <Dialog open={open} onClose={onClose} size="5xl">
      <DialogTitle>
        <div className="flex items-center justify-between gap-4">
          <span>Browse CRM documents</span>
          <Button
            color="slate"
            type="button"
            loading={refreshing}
            disabled={refreshing || !caseId}
            icon={<FontAwesomeIcon icon={faArrowsRotate} className="h-3.5 w-3.5" />}
            onClick={() => void handleRefreshFromCrm()}
            data-testid="available-documents-refresh"
          >
            {refreshing ? "Refreshing…" : "Refresh from CRM"}
          </Button>
        </div>
      </DialogTitle>
      <DialogBody className="flex min-h-0 flex-col gap-4 px-6 py-4 pt-0">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Documents from the linked CRM account. Selected files are added to the queue until you submit.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <FormInput
            type="search"
            aria-label="Search CRM documents"
            placeholder="Search by filename or subject…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={loading && !data}
            className="min-w-0 flex-1"
            data-testid="available-documents-search"
          />
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <div
              className="flex shrink-0 items-center gap-2"
              data-testid="available-documents-hide-added"
            >
              <span className="text-sm text-slate-600 dark:text-slate-300">Hide already added</span>
              <Switch checked={hideAdded} onChange={setHideAdded}>
                <span className="sr-only">Hide already added</span>
              </Switch>
            </div>
          </div>
        </div>

        {data?.cached_at && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Last updated: {formatDateTime(data.cached_at)}
            {data.source === "live" ? " · Live" : " · Cached"}
          </p>
        )}

        {displayError && (
          <div
            className="rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-200"
            role="alert"
            data-testid="available-documents-error"
          >
            {displayError}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700">
          {loading && !data ? (
            <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
              <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-indigo-500" />
              <span>Loading documents…</span>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              {data?.total_count === 0
                ? "No documents found for this CRM account."
                : hideAdded && rows.some((d) => d.selected_for_case)
                  ? "No documents match filters. Turn off “Hide already added” or adjust search."
                  : "No documents match your search."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-700" data-testid="available-documents-list">
              {visibleRows.map((doc) => {
                const metaLine = formatAvailableDocumentMeta(doc, formatDateTime);
                const isAdded = doc.selected_for_case;
                const isSelected = selectedIds.has(doc.document_id);
                return (
                <li
                  key={doc.document_id}
                  className="group flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {!isAdded ? (
                      <Checkbox
                        checked={isSelected}
                        disabled={ingestBlocked || Boolean(addingId)}
                        onChange={(e) => toggleSelected(doc.document_id, e.target.checked)}
                        aria-label={`Select ${doc.filename || doc.subject}`}
                        data-testid={`available-doc-select-${doc.document_id}`}
                        className="mt-0.5"
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900 dark:text-white">
                        {availableDocumentQueueLabel(doc)}
                      </p>
                      {metaLine ? (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {metaLine}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {isAdded ? (
                      <span
                        className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-600 dark:text-slate-100"
                        data-testid="available-doc-badge-added"
                      >
                        Added
                      </span>
                    ) : addingId === doc.document_id ? (
                        <span
                          className="inline-flex h-9 min-w-[8.5rem] items-center justify-center gap-2 rounded-xl bg-slate-500 px-3 text-sm font-medium text-white shadow-sm dark:bg-slate-600"
                          aria-busy="true"
                          aria-live="polite"
                          data-testid={`available-doc-adding-${doc.document_id}`}
                        >
                          <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
                          Adding…
                        </span>
                      ) : (
                        <Button
                          color="slate"
                          type="button"
                          disabled={ingestBlocked || Boolean(addingId)}
                          onClick={() => handleAddOne(doc)}
                          className="pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"
                          data-testid={`available-doc-add-${doc.document_id}`}
                        >
                          Add to queue
                        </Button>
                      )}
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {data != null && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing {visibleRows.length} of {data.total_count} documents · {data.selected_count} already in
            case
            {selectedCount > 0 ? ` · ${selectedCount} selected` : ""}
          </p>
        )}
      </DialogBody>
      <DialogActions>
        <Button color="slate" type="button" onClick={onClose} data-testid="available-documents-close">
          Close
        </Button>
        <Button
          color="indigo"
          type="button"
          disabled={selectedCount === 0 || ingestBlocked}
          onClick={handleAddSelected}
          data-testid="available-documents-add-selected"
        >
          {selectedCount > 0 ? `Add to queue (${selectedCount})` : "Add to queue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
